// @systeme: interface
// L'atelier des prompts : les versions (immuables), la version active, une nouvelle
// version à partir d'une existante, et le banc d'essai pour comparer avant d'activer.
import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { BancEssai, PromptAdmin } from '@foyer/shared';
import { Chargement } from '../../composants/Chargement';
import { useMessages } from '../../composants/Messages';
import { api } from '../../lib/api';
import { formaterJourCourt, jourDe } from '../../lib/dates';

function Serie({ banc }: { banc: BancEssai }) {
  const r = banc.resultat;
  return (
    <Typography variant="body2" color="text.secondary">
      {formaterJourCourt(jourDe(banc.creeLe))} · v{banc.promptVersion}
      {banc.modele ? ` · ${banc.modele}` : ''} ·{' '}
      {banc.statut === 'en_cours'
        ? 'en cours…'
        : banc.statut === 'erreur'
          ? `n’a pas pu tourner (${banc.erreur ?? 'sans détail'})`
          : r
            ? `${r.reussis}/${r.total} réussis · ${r.latenceMs} ms · ${r.cout.toFixed(4)} €`
            : 'terminé'}
    </Typography>
  );
}

export function Prompts() {
  const { dire, direErreur } = useMessages();
  const [prompts, setPrompts] = useState<PromptAdmin[] | undefined>();
  const [bancs, setBancs] = useState<BancEssai[]>([]);
  const [ouvert, setOuvert] = useState<PromptAdmin | undefined>();
  const [brouillon, setBrouillon] = useState<
    { cle: string; contenu: string; note: string } | undefined
  >();

  const charger = useCallback(() => {
    api.admin.prompts().then(setPrompts).catch(direErreur);
    api.admin.bancs().then(setBancs).catch(direErreur);
  }, [direErreur]);

  useEffect(charger, [charger]);

  // Tant qu'une série tourne, on regarde régulièrement.
  useEffect(() => {
    if (!bancs.some((b) => b.statut === 'en_cours')) return;
    const minuterie = setInterval(charger, 5000);
    return () => clearInterval(minuterie);
  }, [bancs, charger]);

  async function activer(p: PromptAdmin) {
    try {
      await api.admin.activerPrompt(p.id);
      dire(`La version ${p.version} de « ${p.cle} » est active.`);
      charger();
    } catch (e) {
      direErreur(e);
    }
  }

  async function lancerBanc(p: PromptAdmin) {
    try {
      await api.admin.lancerBanc({ cle: p.cle, version: p.version });
      dire('La série est lancée. Le résultat arrive ici dans quelques minutes.');
      charger();
    } catch (e) {
      direErreur(e);
    }
  }

  async function creer() {
    if (!brouillon) return;
    try {
      const cree = await api.admin.creerVersionPrompt({
        cle: brouillon.cle,
        contenu: brouillon.contenu,
        ...(brouillon.note.trim() ? { note: brouillon.note.trim() } : {}),
      });
      setBrouillon(undefined);
      dire(`Version ${cree.version} créée. Compare-la au banc avant de l’activer.`);
      charger();
    } catch (e) {
      direErreur(e);
    }
  }

  if (!prompts) return <Chargement />;
  const cles = [...new Set(prompts.map((p) => p.cle))];

  return (
    <Stack spacing={3}>
      <Typography color="text.secondary">
        Une version ne se modifie jamais : on en crée une nouvelle, on la compare au banc d’essai,
        puis on l’active. L’app utilise toujours la version active.
      </Typography>
      {cles.map((cle) => (
        <Paper key={cle} variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h2" component="h2" gutterBottom>
            {cle}
          </Typography>
          <Stack spacing={1}>
            {prompts
              .filter((p) => p.cle === cle)
              .map((p) => (
                <Stack key={p.id} spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 600 }}>Version {p.version}</Typography>
                    {p.actif && <Chip size="small" color="primary" label="active" />}
                    <Typography variant="body2" color="text.secondary">
                      {formaterJourCourt(jourDe(p.creeLe))}
                      {p.note ? ` · ${p.note}` : ''}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setOuvert(ouvert?.id === p.id ? undefined : p)}
                    >
                      {ouvert?.id === p.id ? 'Replier' : 'Lire'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setBrouillon({ cle: p.cle, contenu: p.contenu, note: '' })}
                    >
                      Nouvelle version à partir de celle-ci
                    </Button>
                    <Button size="small" onClick={() => void lancerBanc(p)}>
                      Lancer le banc
                    </Button>
                    {!p.actif && (
                      <Button size="small" variant="outlined" onClick={() => void activer(p)}>
                        Activer
                      </Button>
                    )}
                  </Stack>
                  {ouvert?.id === p.id && (
                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                        m: 0,
                        p: 1,
                        bgcolor: 'action.hover',
                      }}
                    >
                      {p.contenu}
                    </Typography>
                  )}
                  {bancs
                    .filter((b) => b.promptCle === p.cle && b.promptVersion === p.version)
                    .slice(0, 3)
                    .map((b) => (
                      <Serie key={b.id} banc={b} />
                    ))}
                </Stack>
              ))}
          </Stack>
        </Paper>
      ))}
      {brouillon && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h2" component="h2" gutterBottom>
            Nouvelle version de « {brouillon.cle} »
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              multiline
              minRows={10}
              value={brouillon.contenu}
              onChange={(e) => setBrouillon({ ...brouillon, contenu: e.target.value })}
              inputProps={{ 'aria-label': 'Contenu du prompt' }}
            />
            <TextField
              label="Ce que cette version change (une phrase)"
              size="small"
              value={brouillon.note}
              onChange={(e) => setBrouillon({ ...brouillon, note: e.target.value })}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => void creer()}>
                Créer la version
              </Button>
              <Button color="inherit" onClick={() => setBrouillon(undefined)}>
                Annuler
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
